import { useState, useEffect, useCallback, useRef } from 'react';
import { fmtMonto } from 'shared/format';
import {
  FiLock, FiX, FiSearch, FiUser, FiTruck,
  FiCalendar, FiMapPin, FiCheck, FiClock,
  FiFileText, FiEye, FiShoppingBag, FiDollarSign, FiCreditCard, FiPackage,
} from 'react-icons/fi';
import api from '../shared/api';
import { Badge, EmptyState } from '../shared/components/ui';
import VisorComprobante from '../shared/components/VisorComprobante';
import { COL } from 'shared/darkColors';

const resolveTotal = (entry) => {
  if (entry == null) return 0;
  if (typeof entry === 'object') return entry.total_equiv || 0;
  return parseFloat(entry) || 0;
};

const resolveTotalNative = (entry) => {
  if (entry == null) return 0;
  if (typeof entry === 'object') return entry.total || 0;
  return parseFloat(entry) || 0;
};

const totalesEquivSum = (totales) => {
  return Object.values(totales || {}).reduce((s, v) => s + resolveTotal(v), 0);
};

const fmt = (n, symbol) => {
  if (n == null || isNaN(n)) return `${symbol || '$'}0.00`;
  const v = parseFloat(n);
  const s = symbol || '$';
  return v >= 0 ? `${s}${fmtMonto(v)}` : `-${s}${fmtMonto(Math.abs(v))}`;
};

const metodosDePedido = (pd) => {
  const pagos = (Array.isArray(pd.pedido_pagos) && pd.pedido_pagos.length > 0)
    ? pd.pedido_pagos.map((p) => ({ metodo: p.metodo_nombre || p.metodo_codigo }))
    : (Array.isArray(pd.pagos) && pd.pagos.length > 0)
      ? pd.pagos
      : (pd.metodo_pago ? [{ metodo: pd.metodo_pago }] : []);
  return pagos.map((p) => p.metodo).filter(Boolean);
};

const MCOLORS = ['#22c55e','#3b82f6','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6','#f97316','#6366f1'];
const MBGS   = ['#dcfce7','#dbeafe','#ede9fe','#fef3c7','#fee2e2','#cffafe','#fce7f3','#ccfbf1','#ffedd5','#e0e7ff'];

export default function CierreCajaAdmin() {
  const [cajas, setCajas] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('activas');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cajaDetalle, setCajaDetalle] = useState(null);
  const [detalleTab, setDetalleTab] = useState('general');
  const [metodosPago, setMetodosPago] = useState([]);
  const [seleccionados, setSeleccionados] = useState({});
  const [reporte, setReporte] = useState(null);
  const [cargandoReporte, setCargandoReporte] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const cargarActivas = useCallback(() => {
    api.get('/cierre-caja/resumen')
      .then((r) => { if (mountedRef.current) setCajas(r.data?.data || []); })
      .catch(() => { if (mountedRef.current) setError('Error al cargar cajas'); });
  }, []);

  const cargarHistorial = useCallback(() => {
    const params = new URLSearchParams();
    params.append('limite', '100');
    if (filtroUsuario) params.append('usuario_cedula', filtroUsuario);
    if (filtroEstado) params.append('estado', filtroEstado);
    api.get(`/cierre-caja/listar?${params.toString()}`)
      .then((r) => { if (mountedRef.current) setHistorial(r.data?.data || []); })
      .catch(() => {});
  }, [filtroUsuario, filtroEstado]);

  useEffect(() => {
    setLoading(true);
    api.get('/cierre-caja/metodos').then((r) => setMetodosPago(r.data?.data || [])).catch(() => {});
    cargarActivas();
    cargarHistorial();
    setLoading(false);
  }, [cargarActivas, cargarHistorial]);

  useEffect(() => {
    const t = setInterval(cargarActivas, 30000);
    return () => clearInterval(t);
  }, [cargarActivas]);

  const abrirDetalle = (id, tab = 'general') => {
    setDetalleTab(tab);
    setCajaDetalle(null);
    setError('');
    api.get(`/cierre-caja/${id}`)
      .then((r) => setCajaDetalle(r.data?.data))
      .catch(() => setError('No se pudo cargar el detalle del cierre. Verifica tu conexion.'));
  };

  const selIds = Object.keys(seleccionados).map(Number).filter((id) => seleccionados[id]);
  const toggleSeleccion = (h) => {
    if (h.estado !== 'cerrado') return;
    setSeleccionados((prev) => {
      const next = { ...prev };
      if (next[h.id]) delete next[h.id];
      else next[h.id] = true;
      return next;
    });
  };

  const generarReporte = async () => {
    if (selIds.length === 0 || cargandoReporte) return;
    setCargandoReporte(true);
    try {
      const r = await api.post('/cierre-caja/reporte', { cierre_ids: selIds });
      setReporte(r.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo generar el reporte');
    } finally {
      setCargandoReporte(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${COL.border}`,
          borderTopColor: COL.pri2, animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize: 13, color: COL.muted }}>Cargando...</div>
      </div>
    </div>
  );

  const totalActivasFacturado = cajas.reduce((s, c) => s + totalesEquivSum(c.totales_por_metodo), 0);
  const totalActivasPedidosTienda = cajas.reduce((s, c) => s + (c.total_pedidos_tienda || c.total_pedidos || 0), 0);
  const totalActivasPedidosDelivery = cajas.reduce((s, c) => s + (c.total_pedidos_delivery || 0), 0);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: COL.bg, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', background: COL.white, borderBottom: `1px solid ${COL.border}`,
        flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: COL.pri }}>Resumenes de Caja</span>
        <Tabs tab={tab} setTab={setTab} cajasLen={cajas.length} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {error && <ErrorBanner msg={error} onClear={() => setError('')} />}

        {tab === 'activas' && (
          <div style={{ padding: 14 }}>
            {cajas.length === 0 ? (
              <EmptyState icon={FiLock} title="Sin cajas activas" description="No hay cajas abiertas en este momento" />
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 14 }}>
                  <SummaryCard icon={FiFileText} label="Total facturado" value={fmt(totalActivasFacturado)} color={COL.pri} />
                  <SummaryCard icon={FiFileText} label="Tienda" value={totalActivasPedidosTienda} color={COL.pri2} />
                  <SummaryCard icon={FiTruck} label="En Linea" value={totalActivasPedidosDelivery} color="#8b5cf6" />
                  <SummaryCard icon={FiLock} label="Cajas activas" value={cajas.length} color={COL.green} />
                </div>

                {cajas.map((ca) => (
                  <ActiveCajaCard key={ca.id} ca={ca} metodosPago={metodosPago}
                    onDetalle={() => abrirDetalle(ca.id, 'general')}
                    onVerPedidos={() => abrirDetalle(ca.id, 'pedidos')} />
                ))}
              </>
            )}
          </div>
        )}

        {tab === 'historial' && (
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <FiSearch size={14} style={{ position: 'absolute', left: 12, top: 11, color: COL.muted }} />
                <input value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)}
                  placeholder="Cedula de usuario..."
                  style={{ width: '100%', padding: '10px 10px 10px 34px', borderRadius: 12, border: `1px solid ${COL.border}`,
                    fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: COL.white }} />
              </div>
              <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${COL.border}`,
                  fontSize: 12, fontFamily: 'inherit', background: COL.white, outline: 'none', cursor: 'pointer' }}>
                <option value="">Todos</option>
                <option value="abierto">Abiertas</option>
                <option value="cerrado">Cerradas</option>
              </select>
            </div>

            {historial.length === 0 ? (
              <EmptyState icon={FiLock} title="Sin resultados" description="No se encontraron cierres" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {historial.map((h) => (
                  <HistorialRow key={h.id} h={h} selected={!!seleccionados[h.id]} onToggle={() => toggleSeleccion(h)}
                    onClick={() => abrirDetalle(h.id, 'general')} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {cajaDetalle && (
        <DetalleModal cajaDetalle={cajaDetalle} metodosPago={metodosPago} initialTab={detalleTab}
          onClose={() => { setCajaDetalle(null); setDetalleTab('general'); }} />
      )}

      {selIds.length > 0 && (
        <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 24, zIndex: 1200,
          background: COL.pri, color: '#fff', borderRadius: 16, padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{selIds.length} cierre{selIds.length !== 1 ? 's' : ''} seleccionado{selIds.length !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={generarReporte} disabled={cargandoReporte} style={{
            border: 'none', background: '#fff', color: COL.pri, borderRadius: 12, padding: '10px 16px',
            fontWeight: 800, fontSize: 14, cursor: cargandoReporte ? 'default' : 'pointer', fontFamily: 'inherit',
          }}>
            {cargandoReporte ? 'Generando...' : 'Generar reporte'}
          </button>
          <button onClick={() => setSeleccionados({})} style={{ border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
            Limpiar
          </button>
        </div>
      )}

      {reporte && (
        <ReporteModal reporte={reporte} onClose={() => setReporte(null)} />
      )}
    </div>
  );
}

/* ─── Tabs ─── */
function Tabs({ tab, setTab, cajasLen }) {
  const btn = (active) => ({
    padding: '7px 16px', border: `1.5px solid ${active ? COL.pri : COL.border}`,
    background: active ? COL.pri : 'transparent', color: active ? '#fff' : COL.muted,
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
  });
  return (
    <div style={{ display: 'flex' }}>
      <button onClick={() => setTab('activas')} style={{ ...btn(tab === 'activas'), borderRadius: '10px 0 0 10px' }}>
        Activas ({cajasLen})
      </button>
      <button onClick={() => setTab('historial')} style={{ ...btn(tab === 'historial'), borderRadius: '0 10px 10px 0' }}>
        Historial
      </button>
    </div>
  );
}

/* ─── Summary Card ─── */
function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{ padding: '14px 16px', borderRadius: 14, border: `1px solid ${COL.border}`, background: COL.white }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon size={14} color={color} />
        <span style={{ fontSize: 11, fontWeight: 600, color: COL.muted }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

/* ─── Error ─── */
function ErrorBanner({ msg, onClear }) {
  return (
    <div style={{ margin: '12px 16px', padding: '10px 14px', background: '#fef2f2', borderRadius: 10,
      border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: '#991b1b' }}>{msg}</span>
      <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', padding: 2 }}>
        <FiX size={16} />
      </button>
    </div>
  );
}

/* ─── Active Caja Card ─── */
function ActiveCajaCard({ ca, metodosPago, onDetalle, onVerPedidos }) {
  const totales = ca.totales_por_metodo || {};

  return (
    <div style={{ background: COL.white, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 18, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14,
            background: `linear-gradient(135deg, ${COL.pri2}15, ${COL.pri2}25)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiUser size={20} color={COL.pri2} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>{ca.usuario_nombre || ca.usuario_cedula}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 11, color: COL.muted, display: 'flex', alignItems: 'center', gap: 3 }}>
                <FiMapPin size={10} />{ca.sede_codigo}
              </span>
              <span style={{ fontSize: 11, color: COL.muted, display: 'flex', alignItems: 'center', gap: 3 }}>
                <FiCalendar size={10} />{new Date(ca.fecha_apertura).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            </div>
          </div>
        </div>
        <Badge label="ABIERTA" variant="success" dot />
      </div>

      {Object.keys(totales).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6, marginBottom: 12 }}>
          {Object.entries(totales).sort(([,a],[,b]) => resolveTotal(b) - resolveTotal(a)).map(([nombre, monto], i) => {
            const color = MCOLORS[i % MCOLORS.length];
            const bg = MBGS[i % MBGS.length];
            const equiv = resolveTotal(monto);
            const native = resolveTotalNative(monto);
            const info = (monto && typeof monto === 'object') ? monto : null;
            const simb = info ? (info.moneda_simbolo || '$') : '$';
            const isPrincipal = !info || !info.moneda_codigo || info.moneda_codigo === 'USD' || Math.abs((info.tasa || 1) - 1) < 0.05;
            return (
              <div key={nombre} style={{ textAlign: 'center', padding: '10px 4px', borderRadius: 12,
                background: bg, border: `1px solid ${color}20` }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: COL.text }}>{fmt(native, simb)}</div>
                {!isPrincipal && native !== equiv && <div style={{ fontSize: 9, fontWeight: 500, color: COL.muted }}>{fmt(equiv, '$')}</div>}
                <div style={{ fontSize: 10, fontWeight: 600, color, lineHeight: 1.2, marginTop: 2 }}>{nombre}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 0 0', borderTop: `1px solid ${COL.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: COL.muted }}>
            <span style={{ fontWeight: 700, color: COL.text }}>{ca.total_pedidos_tienda || ca.total_pedidos || 0}</span> tienda
          </span>
          {(ca.total_pedidos_delivery || 0) > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#8b5cf6' }}>
              <FiTruck size={11} />
              <span style={{ fontWeight: 700 }}>{ca.total_pedidos_delivery}</span> en linea
            </span>
          )}
          <span style={{ fontSize: 12, color: COL.muted }}>
            Base: <span style={{ fontWeight: 600, color: COL.text }}>{fmt(ca.base_inicial_usd)} USD</span>
            {ca.base_inicial_bs > 0 ? <span style={{ fontWeight: 600, color: COL.text }}> · {fmt(ca.base_inicial_bs)} Bs</span> : null}
          </span>
          {ca.total_delivery > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8b5cf6' }}>
              <FiTruck size={11} />{fmt(ca.total_delivery)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => onVerPedidos()} style={{
            padding: '7px 14px', borderRadius: 10, border: `1px solid ${COL.border}`,
            background: COL.white, cursor: 'pointer', fontSize: 11, fontWeight: 600,
            color: COL.muted, fontFamily: 'inherit',
          }}>Pedidos</button>
          <button onClick={() => onDetalle()} style={{
            padding: '7px 14px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${COL.pri2}, ${COL.pri})`,
            color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
          }}>Ver detalle</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Historial Row ─── */
function HistorialRow({ h, selected, onToggle, onClick }) {
  const totalFact = totalesEquivSum(h.totales_por_metodo);
  const cerrado = h.estado === 'cerrado';
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
      {cerrado && (
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} title="Seleccionar para reporte"
          style={{ width: 34, border: `1.5px solid ${selected ? COL.pri : COL.border}`, borderRight: 'none',
            borderRadius: '14px 0 0 14px', background: selected ? COL.pri : COL.white, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {selected && <FiCheck size={16} color="#fff" />}
        </button>
      )}
      <div onClick={onClick} style={{ flex: 1, background: COL.white, borderRadius: cerrado ? '0 14px 14px 0' : 14,
        border: `1px solid ${COL.border}`, padding: '14px 16px', cursor: 'pointer', transition: 'background 0.1s' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 11,
            background: cerrado ? '#f3f4f6' : '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {cerrado ? <FiCheck size={16} color="#6b7280" /> : <FiClock size={16} color="#22c55e" />}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>{h.usuario_nombre || h.usuario_cedula}</div>
            <div style={{ fontSize: 11, color: COL.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FiMapPin size={9} />{h.sede_codigo}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FiCalendar size={9} />{new Date(h.fecha_apertura).toLocaleDateString([], { dateStyle: 'short' })}</span>
              {cerrado && h.fecha_cierre && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <FiLock size={9} />{new Date(h.fecha_cierre).toLocaleDateString([], { dateStyle: 'short' })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {h.diferencia_total != null && (
            <span style={{ fontSize: 12, fontWeight: 700,
              color: Math.abs(h.diferencia_total) < 0.01 ? COL.green : COL.red }}>
              {h.diferencia_total >= 0 ? '+' : ''}{fmt(h.diferencia_total)}
            </span>
          )}
          <Badge label={h.estado?.toUpperCase()} variant={cerrado ? 'neutral' : 'success'} dot />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 11, color: COL.muted }}>
        <span><span style={{ fontWeight: 700, color: COL.text }}>{h.total_pedidos_tienda || h.total_pedidos || 0}</span> tienda</span>
        {(h.total_pedidos_delivery || 0) > 0 && (
          <span>
            <span style={{ fontWeight: 700, color: '#8b5cf6' }}>{h.total_pedidos_delivery}</span> en linea
          </span>
        )}
        {totalFact > 0 && <span>Fact: <span style={{ fontWeight: 600, color: COL.text }}>{fmt(totalFact)}</span></span>}
        {h.total_delivery > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#8b5cf6' }}>
            <FiTruck size={10} />{fmt(h.total_delivery)}
          </span>
        )}
      </div>
      </div>
    </div>
  );
}

/* ─── Detalle Modal ─── */
function DetalleModal({ cajaDetalle, metodosPago, initialTab, onClose }) {
  const [tab, setTab] = useState(initialTab || 'general');
  const [visor, setVisor] = useState(null);
  const [showProductos, setShowProductos] = useState(false);
  const cerrado = cajaDetalle.estado === 'cerrado';
  const totales = cajaDetalle.totales_por_metodo || {};
  const arqueo = cajaDetalle.arqueo_por_metodo || {};
  const totalFact = totalesEquivSum(totales);
  const productosVendidos = Array.isArray(cajaDetalle.productos_vendidos) ? cajaDetalle.productos_vendidos : [];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(3px)', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 24, width: '100%',
        maxWidth: 880, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COL.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: COL.pri }}>Cierre #{cajaDetalle.id}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge label={cajaDetalle.estado?.toUpperCase()} variant={cerrado ? 'neutral' : 'success'} dot />
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, padding: 4 }}>
              <FiX size={22} />
            </button>
          </div>
        </div>

        <ModalTabs tab={tab} setTab={setTab} />

        {tab === 'general' ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14,
                background: `linear-gradient(135deg, ${COL.pri2}15, ${COL.pri2}25)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiUser size={22} color={COL.pri2} />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: COL.text }}>{cajaDetalle.usuario_nombre || cajaDetalle.usuario_cedula}</div>
                <div style={{ fontSize: 11, color: COL.muted }}>{cajaDetalle.sede_codigo}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COL.muted }}>
                <FiCalendar size={12} color={COL.pri2} />
                Abierta: {cajaDetalle.fecha_apertura ? new Date(cajaDetalle.fecha_apertura).toLocaleString() : '-'}
              </div>
              {cerrado && cajaDetalle.fecha_cierre && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COL.muted }}>
                  <FiLock size={12} color="#6b7280" />
                  Cerrada: {new Date(cajaDetalle.fecha_cierre).toLocaleString()}
                </div>
              )}
              {cajaDetalle.cerrado_por_nombre && (
                <div style={{ fontSize: 11, color: COL.muted }}>
                  Cerrada por: <span style={{ fontWeight: 600, color: COL.text }}>{cajaDetalle.cerrado_por_nombre}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18,
            padding: 14, borderRadius: 14, background: COL.bg, border: `1px solid ${COL.border}` }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: COL.muted, marginBottom: 2 }}>Base USD</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: COL.green }}>{fmt(cajaDetalle.base_inicial_usd || 0)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: COL.muted, marginBottom: 2 }}>Base Bs</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: COL.pri2 }}>{fmt(cajaDetalle.base_inicial_bs || 0)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 18 }}>
            <KpiBox icon={<FiFileText size={16} />} label="Tienda" value={cajaDetalle.total_pedidos_tienda || cajaDetalle.total_pedidos || 0} color={COL.pri2} />
            <KpiBox icon={<FiTruck size={16} />} label="En Linea" value={cajaDetalle.total_pedidos_delivery || 0} color="#8b5cf6" />
            <KpiBox icon={<FiX size={16} />} label="Anulados" value={cajaDetalle.total_anulados || 0} color="#ef4444" />
          </div>

          {productosVendidos.length > 0 && (
            <button onClick={() => setShowProductos(true)} style={{
              width: '100%', marginBottom: 18, padding: '12px 14px', borderRadius: 12,
              border: `1px solid ${COL.pri2}30`, background: `${COL.pri2}08`,
              cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 10,
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: COL.pri2 }}>
                <FiPackage size={15} /> Productos vendidos
              </span>
              <span style={{ fontSize: 12, fontWeight: 800, color: COL.pri2 }}>
                {productosVendidos.length} {productosVendidos.length === 1 ? 'producto' : 'productos'}
              </span>
            </button>
          )}

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, marginBottom: 10 }}>Totales facturados</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(totales).sort(([,a],[,b]) => resolveTotal(b) - resolveTotal(a)).map(([nombre, monto], i) => {
                const equi = resolveTotal(monto);
                const nati = resolveTotalNative(monto);
                const pct = totalFact > 0 ? Math.round((equi / totalFact) * 100) : 0;
                const color = MCOLORS[i % MCOLORS.length];
                const bg = MBGS[i % MBGS.length];
                const info = (monto && typeof monto === 'object') ? monto : null;
                const simb = info ? (info.moneda_simbolo || '$') : '$';
                const isPrincipal = !info || !info.moneda_codigo || info.moneda_codigo === 'USD' || Math.abs((info.tasa || 1) - 1) < 0.05;
                return (
                  <div key={nombre} style={{ display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 12, background: `${color}08`, border: `1px solid ${color}15` }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color }}>{nombre.charAt(0).toUpperCase()}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{nombre}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: COL.text }}>{fmt(nati, simb)}</span>
                      </div>
                      {!isPrincipal && nati !== equi && (
                        <div style={{ fontSize: 10, color: COL.muted }}>{fmt(equi, '$')}</div>
                      )}
                      <div style={{ height: 5, borderRadius: 3, background: COL.border, marginTop: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 3, background: color, width: `${pct}%`, minWidth: equi > 0 ? 4 : 0 }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(cajaDetalle.total_delivery > 0 || (cajaDetalle.delivery_detalle && cajaDetalle.delivery_detalle.length > 0)) && (
            <div style={{ marginBottom: 18, padding: 14, borderRadius: 14, background: '#f5f3ff', border: '1px solid #ddd6fe' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>
                <FiTruck size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />Delivery del dia
              </div>
              <div style={{ fontSize: 11, color: COL.muted, marginBottom: 8 }}>Costo de envio recolectado por cada repartidor</div>
              {(cajaDetalle.delivery_detalle || []).map((dv, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12, padding: '6px 0', color: COL.text,
                  borderBottom: i < (cajaDetalle.delivery_detalle || []).length - 1 ? `1px solid #ede9fe` : 'none' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {dv.es_encomienda ? <FiPackage size={12} color="#7c3aed" /> : <FiTruck size={12} color="#7c3aed" />}
                    {dv.delivery_nombre}
                  </span>
                  <span style={{ fontWeight: 700, color: '#7c3aed' }}>{fmt(dv.costo_envio || 0)}</span>
                </div>
              ))}
            </div>
          )}

          {cerrado && Object.keys(arqueo).length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, marginBottom: 10 }}>Arqueo</div>
              {Object.entries(arqueo).map(([nombre, a], i) => {
                const fact = a.facturado || 0;
                const rec = a.recibido || 0;
                const dif = a.diferencia || 0;
                const arqSimbolo = (a && a.moneda_simbolo) || '$';
                const difOk = Math.abs(dif) < 0.01;
                const color = MCOLORS[i % MCOLORS.length];
                const bg = MBGS[i % MBGS.length];
                return (
                  <div key={nombre} style={{ display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 0', borderBottom: `1px solid ${COL.border}` }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color }}>{nombre.charAt(0).toUpperCase()}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{nombre}</div>
                      <div style={{ fontSize: 11, color: COL.muted }}>Fact: {fmt(fact, arqSimbolo)} · Rec: {fmt(rec, arqSimbolo)}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: difOk ? COL.green : COL.red }}>
                        {dif >= 0 ? '+' : ''}{fmt(dif, arqSimbolo)}
                      </span>
                      {a.comprobante ? (
                        <button onClick={(e) => { e.stopPropagation(); setVisor(a.comprobante); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px',
                            borderRadius: 9, border: `1px solid ${COL.pri2}40`, background: `${COL.pri2}0a`,
                            cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                            color: COL.pri2 }}>
                          <FiEye size={13} /> Ver comprobante
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: COL.muted, opacity: 0.6 }}>Sin comprobante</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0',
                marginTop: 4, borderTop: `2px solid ${COL.pri}20` }}>
                <span style={{ fontWeight: 700, color: COL.text }}>Diferencia total</span>
                <span style={{ fontWeight: 800, fontSize: 16,
                  color: Math.abs(cajaDetalle.diferencia_total || 0) < 0.01 ? COL.green : COL.red }}>
                  {(cajaDetalle.diferencia_total || 0) >= 0 ? '+' : ''}{fmt(cajaDetalle.diferencia_total || 0)}
                </span>
              </div>
            </div>
          )}

          {cajaDetalle.notas && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COL.muted, marginBottom: 4 }}>Notas</div>
              <div style={{ padding: '10px 14px', borderRadius: 12, background: COL.bg,
                border: `1px solid ${COL.border}`, fontSize: 13, color: COL.text, fontStyle: 'italic' }}>
                "{cajaDetalle.notas}"
              </div>
            </div>
          )}
        </div>
        ) : (
          <PedidosTab cierreId={cajaDetalle.id} />
        )}
      </div>
      {visor && <VisorComprobante comprobanteUrl={visor} onClose={() => setVisor(null)} />}
      {showProductos && (
        <ProductosVendidosModal productos={productosVendidos} onClose={() => setShowProductos(false)} />
      )}
    </div>
  );
}

/* ─── Productos vendidos modal ─── */
function ProductosVendidosModal({ productos, onClose }) {
  const [busqueda, setBusqueda] = useState('');
  const q = busqueda.trim().toLowerCase();
  const filtrados = productos.filter((p) => {
    const nombre = String(p.nombre || p.codigo || '').toLowerCase();
    const codigo = String(p.codigo || '').toLowerCase();
    return !q || nombre.includes(q) || codigo.includes(q);
  });
  const totalMonto = productos.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const totalUnidades = productos.reduce((s, p) => s + (parseFloat(p.cantidad) || 0), 0);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(3px)', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COL.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: COL.pri }}>Productos vendidos</div>
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 1 }}>
              {productos.length} producto{productos.length !== 1 ? 's' : ''} · {totalUnidades} und{totalUnidades !== 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, padding: 4 }}>
            <FiX size={22} />
          </button>
        </div>

        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COL.border}`, flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 12, top: 11, color: COL.muted }} />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar producto por nombre o codigo..."
              autoFocus
              style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 12,
                border: `1px solid ${COL.border}`, fontSize: 13, fontFamily: 'inherit',
                boxSizing: 'border-box', outline: 'none', background: COL.bg, color: COL.text }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {filtrados.length === 0 ? (
            <EmptyState icon={FiSearch} title="Sin coincidencias" description="Ningun producto coincide con la busqueda" />
          ) : (
            filtrados.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                borderBottom: i < filtrados.length - 1 ? `1px solid ${COL.border}` : 'none' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COL.pri2}12`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FiPackage size={16} color={COL.pri2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COL.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.nombre || p.codigo || 'Producto'}
                  </div>
                  <div style={{ fontSize: 11, color: COL.muted }}>
                    {p.pedidos} {p.pedidos === 1 ? 'pedido' : 'pedidos'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: COL.text }}>{p.cantidad} und</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COL.green }}>{fmt(p.monto)}</div>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '12px 16px', borderTop: `1px solid ${COL.border}`, flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: COL.bg }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
            Total vendido ({totalUnidades} unds)
          </span>
          <span style={{ fontSize: 15, fontWeight: 800, color: COL.green }}>{fmt(totalMonto)}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Reporte Multi-cierre Modal ─── */
function ReporteModal({ reporte, onClose }) {
  const [busqueda, setBusqueda] = useState('');
  const totales = reporte.totales_por_metodo || {};
  const productos = Array.isArray(reporte.productos_vendidos) ? reporte.productos_vendidos : [];
  const q = busqueda.trim().toLowerCase();
  const productosFiltrados = productos.filter((p) => {
    const nombre = String(p.nombre || p.codigo || '').toLowerCase();
    const codigo = String(p.codigo || '').toLowerCase();
    return !q || nombre.includes(q) || codigo.includes(q);
  });
  const totalMonto = productos.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const totalUnidades = productos.reduce((s, p) => s + (parseFloat(p.cantidad) || 0), 0);
  const totalFact = totalesEquivSum(totales);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(3px)', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 24, width: '100%', maxWidth: 720, maxHeight: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${COL.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: COL.pri }}>Reporte de cierres</div>
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 1 }}>{reporte.num_cierres} cierre{reporte.num_cierres !== 1 ? 's' : ''} seleccionado{reporte.num_cierres !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, padding: 4 }}>
            <FiX size={22} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
            <KpiBox icon={<FiDollarSign size={16} />} label="Facturado" value={fmt(totalFact)} color={COL.pri2} />
            <KpiBox icon={<FiFileText size={16} />} label="Pedidos" value={reporte.total_pedidos || 0} color={COL.green} />
            <KpiBox icon={<FiX size={16} />} label="Anulados" value={reporte.total_anulados || 0} color="#ef4444" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div style={{ padding: '12px 14px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COL.red, marginBottom: 2 }}>Falta de pago</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: COL.red }}>-{fmt(reporte.total_faltante || 0)}</div>
            </div>
            <div style={{ padding: '12px 14px', borderRadius: 12, background: '#fff7ed', border: '1px solid #fed7aa' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginBottom: 2 }}>Sobrante (no propina)</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#d97706' }}>+{fmt(reporte.total_sobrante || 0)}</div>
            </div>
          </div>

          {Object.keys(totales).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, marginBottom: 8 }}>Totales por metodo</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(totales).sort(([,a],[,b]) => resolveTotal(b) - resolveTotal(a)).map(([nombre, info], i) => {
                  const color = MCOLORS[i % MCOLORS.length];
                  const bg = MBGS[i % MBGS.length];
                  const simb = (info && info.moneda_simbolo) || '$';
                  const nati = resolveTotalNative(info);
                  const equi = resolveTotal(info);
                  const isPrincipal = !info || !info.moneda_codigo || info.moneda_codigo === 'USD' || Math.abs((info.tasa || 1) - 1) < 0.05;
                  return (
                    <div key={nombre} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                      borderRadius: 10, background: `${color}08`, border: `1px solid ${color}15` }}>
                      <div style={{ width: 30, height: 30, borderRadius: 9, background: bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color }}>{nombre.charAt(0).toUpperCase()}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{nombre}</span>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: COL.text }}>{fmt(nati, simb)}</div>
                        {!isPrincipal && nati !== equi && <div style={{ fontSize: 10, color: COL.muted }}>{fmt(equi, '$')}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {productos.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, marginBottom: 8 }}>Productos vendidos</div>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <FiSearch size={14} style={{ position: 'absolute', left: 12, top: 10, color: COL.muted }} />
                <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar producto..."
                  style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: 10, border: `1px solid ${COL.border}`,
                    fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: COL.bg, color: COL.text }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {productosFiltrados.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                    borderBottom: i < productosFiltrados.length - 1 ? `1px solid ${COL.border}` : 'none' }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: `${COL.pri2}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FiPackage size={14} color={COL.pri2} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COL.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.nombre || p.codigo || 'Producto'}
                      </div>
                      <div style={{ fontSize: 10, color: COL.muted }}>{p.pedidos} pedido{p.pedidos !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: COL.text }}>{p.cantidad} und</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: COL.green }}>{fmt(p.monto)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0',
                borderTop: `2px solid ${COL.pri}20`, marginTop: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: COL.text }}>Total ({totalUnidades} unds)</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: COL.green }}>{fmt(totalMonto)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Modal Tabs ─── */
function ModalTabs({ tab, setTab }) {
  const btn = (active) => ({
    flex: 1, padding: '9px 0', border: 'none', borderRadius: 10,
    background: active ? COL.pri : 'transparent', color: active ? '#fff' : COL.muted,
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, transition: 'all 0.15s',
  });
  return (
    <div style={{ margin: '0 18px 12px', padding: 4, background: COL.bg, borderRadius: 12,
      display: 'flex', gap: 4, flexShrink: 0 }}>
      <button onClick={() => setTab('general')} style={btn(tab === 'general')}>General</button>
      <button onClick={() => setTab('pedidos')} style={btn(tab === 'pedidos')}>Pedidos</button>
    </div>
  );
}

/* ─── Pedidos Tab ─── */
function PedidosTab({ cierreId }) {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visor, setVisor] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/cierre-caja/${cierreId}/pedidos`)
      .then((r) => setPedidos(r.data?.data || []))
      .catch(() => setPedidos([]))
      .finally(() => setLoading(false));
  }, [cierreId]);

  const totalFacturado = pedidos.reduce((s, p) => s + (parseFloat(p.total) || 0) + (parseFloat(p.costo_envio) || 0), 0);
  const nTienda = pedidos.filter((p) => p.tipo_pedido === 'tienda').length;
  const nLinea = pedidos.filter((p) => p.tipo_pedido !== 'tienda').length;

  const esDeliveryPedido = (p) => p.tipo_pedido !== 'tienda' && (p.tipo_entrega === 'delivery' || p.id_delivery);

  const metodosPagoDisponibles = [...new Set(pedidos.flatMap(metodosDePedido))].sort();

  const q = filtro.trim().toLowerCase();
  const filtrados = pedidos.filter((p) => {
    const esTienda = p.tipo_pedido === 'tienda';
    if (filtroTipo === 'delivery' && !esDeliveryPedido(p)) return false;
    if (filtroTipo === 'tienda' && !esTienda) return false;
    if (filtroTipo === 'online' && esTienda) return false;
    if (filtroMetodo && !metodosDePedido(p).some((m) => String(m).toLowerCase() === filtroMetodo.toLowerCase())) return false;
    if (!q) return true;
    const nombre = (p.cliente_nombre || p.cliente || '').toLowerCase();
    const ticket = String(p.ticket_numero || p.id);
    return nombre.includes(q) || ticket.includes(q);
  });

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px 22px' }}>
      {!loading && pedidos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
          <SummaryCard icon={FiFileText} label="Pedidos" value={pedidos.length} color={COL.pri} />
          <SummaryCard icon={FiDollarSign} label="Facturado" value={fmt(totalFacturado)} color={COL.green} />
          <SummaryCard icon={FiShoppingBag} label="Tienda" value={nTienda} color={COL.pri2} />
          <SummaryCard icon={FiTruck} label="En linea" value={nLinea} color="#8b5cf6" />
        </div>
      )}

      {!loading && pedidos.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 12, top: 11, color: COL.muted }} />
            <input value={filtro} onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar por cliente o #ticket..."
              style={{ width: '100%', padding: '10px 10px 10px 34px', borderRadius: 12, border: `1px solid ${COL.border}`,
                fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: COL.white }} />
          </div>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${COL.border}`,
              fontSize: 12, fontFamily: 'inherit', background: COL.white, outline: 'none', cursor: 'pointer' }}>
            <option value="">Todos</option>
            <option value="tienda">Tienda</option>
            <option value="delivery">Delivery</option>
            <option value="online">En linea</option>
          </select>
          <select value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${COL.border}`,
              fontSize: 12, fontFamily: 'inherit', background: COL.white, outline: 'none', cursor: 'pointer', maxWidth: 180 }}>
            <option value="">Método de pago</option>
            {metodosPagoDisponibles.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: COL.muted, fontSize: 13 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', border: `3px solid ${COL.border}`,
            borderTopColor: COL.pri2, animation: 'spin 0.7s linear infinite', margin: '0 auto 10px' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Cargando pedidos...
        </div>
      ) : filtrados.length === 0 ? (
        <EmptyState icon={FiFileText} title="Sin pedidos"
          description={pedidos.length === 0 ? 'Este cierre no tiene pedidos asociados' : 'Ningun pedido coincide con el filtro'} />
      ) : (
        filtrados.map((pd) => <PedidoRow key={pd.id} pd={pd} onVerComprobante={setVisor} />)
      )}
      {visor && <VisorComprobante comprobanteUrl={visor} onClose={() => setVisor(null)} />}
    </div>
  );
}

function PedidoRow({ pd, onVerComprobante }) {
  const esTienda = pd.tipo_pedido === 'tienda';
  const esDelivery = pd.tipo_pedido !== 'tienda' && (pd.tipo_entrega === 'delivery' || pd.id_delivery);

  const splitPagos = (Array.isArray(pd.pedido_pagos) && pd.pedido_pagos.length > 0)
    ? pd.pedido_pagos.map((p) => ({
        metodo: p.metodo_nombre || p.metodo_codigo || 'N/A',
        monto: p.monto,
        comprobante_url: p.comprobante_url || null,
        comprobantes: Array.isArray(p.comprobantes) ? p.comprobantes : [],
      }))
    : [];

  const pagos = splitPagos.length > 0
    ? splitPagos
    : (Array.isArray(pd.pagos) && pd.pagos.length > 0)
      ? pd.pagos
      : (pd.metodo_pago ? [{ metodo: pd.metodo_pago, monto: pd.total, monto_equiv: pd.total, moneda_simbolo: '$', tasa: 1 }] : []);

  const compsDePago = (p) => {
    const urls = (Array.isArray(p.comprobantes) ? p.comprobantes : [])
      .map((c) => c.url || c.comprobante_url).filter(Boolean);
    if (urls.length === 0 && p.comprobante_url) urls.push(p.comprobante_url);
    return urls;
  };

  const compsLegacy = (Array.isArray(pd.comprobantes) ? pd.comprobantes : [])
    .map((c) => c.url).filter(Boolean);

  const icono = esTienda ? <FiShoppingBag size={18} color={COL.pri2} /> : esDelivery ? <FiTruck size={18} color="#8b5cf6" /> : <FiCreditCard size={18} color="#06b6d4" />;
  const iconoBg = esTienda ? `${COL.pri2}15` : esDelivery ? '#f5f3ff' : '#ecfeff';
  const tipoBadge = esTienda ? <Badge label="TIENDA" variant="info" /> : <Badge label="EN LINEA" variant="neutral" />;

  return (
    <div style={{ background: COL.bg, borderRadius: 16, border: `1px solid ${COL.border}`, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: iconoBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {icono}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: COL.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {pd.cliente_nombre || pd.cliente || 'Sin cliente'}
              </span>
              {tipoBadge}
              {esDelivery && <Badge label="DELIVERY" variant="warning" />}
            </div>
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
              #{pd.ticket_numero || pd.id}
              {pd.cliente_telefono ? ` · ${pd.cliente_telefono}` : ''}
              {pd.hora_cobro ? ` · ${new Date(pd.hora_cobro).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
            </div>
            {esDelivery && pd.delivery_nombre && (
              <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiTruck size={11} /> {pd.delivery_nombre}
              </div>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: COL.pri }}>{fmt((parseFloat(pd.total) || 0) + (parseFloat(pd.costo_envio) || 0))}</div>
          {(parseFloat(pd.costo_envio) || 0) > 0 && (
            <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 1 }}>Incluye envio {fmt(pd.costo_envio)}</div>
          )}
          <div style={{ fontSize: 10, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 1 }}>
            {pd.estado?.replace(/_/g, ' ')}
          </div>
        </div>
      </div>

      {Array.isArray(pd.productos) && pd.productos.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {pd.productos.slice(0, 4).map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
              <span style={{ color: COL.text }}>{item.cantidad || 1}x {item.nombre || item.descripcion || item.codigo}</span>
              <span>{fmt(item.precio_unitario || item.precio || 0)}</span>
            </div>
          ))}
          {pd.productos.length > 4 && (
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>+{pd.productos.length - 4} mas...</div>
          )}
        </div>
      )}

      {pagos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {pagos.map((p, i) => {
            const secundaria = p.tasa && p.tasa > 1.05;
            const simb = p.moneda_simbolo || '$';
            const esEfectivo = /efectivo|cash/i.test(String(p.metodo || ''));
            const comps = compsDePago(p);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 11, background: COL.white, border: `1px solid ${COL.border}` }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  background: esEfectivo ? '#dcfce7' : '#eef2ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {esEfectivo ? <FiDollarSign size={15} color="#22c55e" /> : <FiCreditCard size={14} color="#6366f1" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{p.metodo || 'N/A'}</span>
                    {i === 0 && <span style={{ fontSize: 9, fontWeight: 700, color: COL.pri2,
                      background: `${COL.pri2}15`, padding: '2px 7px', borderRadius: 20 }}>PRINCIPAL</span>}
                  </div>
                  {comps.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {comps.map((url, ci) => (
                        <button key={ci} onClick={() => onVerComprobante(url)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 7,
                            border: `1px solid ${COL.pri2}40`, background: `${COL.pri2}0a`, cursor: 'pointer',
                            fontSize: 10, fontWeight: 600, color: COL.pri2, fontFamily: 'inherit', flexShrink: 0 }}>
                          <FiEye size={12} /> Comprobante{comps.length > 1 ? ` ${ci + 1}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>{fmt(p.monto, simb)}</div>
                  {secundaria && <div style={{ fontSize: 10, color: COL.muted }}>≈ {fmt(p.monto_equiv || p.monto, '$')}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTop: `1px solid ${COL.border}`, paddingTop: 10 }}>
        <span style={{ fontSize: 11, color: COL.muted }}>
          {pagos.length > 1 ? `${pagos.length} pagos` : '1 pago'}
        </span>
        {compsLegacy.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
            {compsLegacy.map((url, i) => (
              <button key={i} onClick={() => onVerComprobante(url)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
                  border: `1px solid ${COL.border}`, background: COL.white, cursor: 'pointer',
                  fontSize: 11, fontWeight: 600, color: COL.pri2, fontFamily: 'inherit' }}>
                <FiEye size={14} /> Ver comprobante{compsLegacy.length > 1 ? ` ${i + 1}` : ''}
              </button>
            ))}
          </div>
        ) : pd.comprobante_url ? (
          <button onClick={() => onVerComprobante(pd.comprobante_url)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
              border: `1px solid ${COL.border}`, background: COL.white, cursor: 'pointer',
              fontSize: 11, fontWeight: 600, color: COL.pri2, fontFamily: 'inherit' }}>
            <FiEye size={14} /> Ver comprobante
          </button>
        ) : pagos.some((p) => compsDePago(p).length > 0) ? (
          <span style={{ fontSize: 11, color: COL.muted, opacity: 0.7 }}>Comprobante en el pago</span>
        ) : (
          <span style={{ fontSize: 11, color: COL.muted, opacity: 0.7 }}>Sin comprobante</span>
        )}
      </div>
    </div>
  );
}

/* ─── KPI Box ─── */
function KpiBox({ icon, label, value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10,
      padding: 14, borderRadius: 14, background: `${color}08`, border: `1px solid ${color}20` }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 600, color: COL.muted }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: COL.text }}>{value}</div>
      </div>
    </div>
  );
}
